import { useRouter } from 'next/router';
import React from 'react';
import DealPageCol from '@/components/Table/DealPageCol';
import DealTable from '@/components/Table/DealTable';
import { CollectionHeader } from '@/components/UI/headers/CollectionHeader';

import { EmptyBidCard } from '@/components/cards/EmptyBidCard';
import LicensedNft from '@/components/cards/LicensedNft';
import SeoHead from '@/components/head/SeoHead';
import LoadingView from '@/components/layout/LoadingView';
import PageContainer from '@/components/layout/PageContainer';
import { usePageEntrance } from '@/hooks/usePageEntrance';
import type { DealFragment } from '@/utilities/__generated__/graphql';
import { useGetDealsByCollectionQuery } from '@/utilities/__generated__/graphql';

import { sortDeals } from '@/utilities/functions';
import type { NextPage } from 'next';

const CollectionPage: NextPage = () => {
  const router = useRouter();
  const queryAddress =
    typeof router.query.address === 'string' ? router.query.address : '';
  const { data, loading } = useGetDealsByCollectionQuery({
    variables: {
      nft_contract_address: queryAddress,
    },
    skip: !queryAddress,
  });

  const collection = data?.Collections[0];
  const sortedDeals = sortDeals(data?.Deals || []);
  usePageEntrance();
  return (
    <>
      <SeoHead title={`OverpassIP | ${collection?.name}`} />
      <PageContainer>
        <div className="flex flex-col space-y-9">
          <CollectionHeader
            nftContractAddress={
              collection?.nft_contract_address ?? queryAddress
            }
            title={collection?.name ?? ''}
            summary={collection?.description ?? ''}
            twitterHandle={collection?.twitter ?? ''}
            instagramHandle={collection?.instagram ?? ''}
            website={collection?.website ?? ''}
          />
          <div className="flex w-full items-center justify-between rounded-2xl bg-white p-6 pb-6">
            <DealTable
              heading="Collection Opportunities"
              deals={sortedDeals as DealFragment[]}
              isLoading={loading}
            />
          </div>
          <DealPageCol title="Licensed Assets">
            <div className="w-full px-4 pb-6 pt-8 md:px-6">
              {loading ? (
                <LoadingView />
              ) : data?.executed_bids.length ? (
                <div className="hidden-scroll flex snap-x snap-mandatory space-x-4 overflow-scroll">
                  <div className="hidden-scroll flex w-full snap-x snap-mandatory gap-2 md:grid md:grid-cols-3 md:gap-4 md:overflow-scroll">
                    {data?.executed_bids.map((data) => (
                      <div
                        key={`${data.token_id}${data.Deal.id}`}
                        className="col-span-1 snap-start"
                      >
                        <LicensedNft
                          tokenId={data.token_id}
                          collectionAddress={data.nft_contract_address}
                          tokenName={String(data.Collection.name)}
                          brand={data.Deal.Brand}
                          dealName={data.Deal.title}
                          dealId={data.Deal.id}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex w-full items-center justify-center">
                  <EmptyBidCard
                    //TODO uncomment when edit page is implemented
                    // hasButton={authed}
                    title={'No Licensed Assets'}
                  />
                </div>
              )}
            </div>
          </DealPageCol>
        </div>
      </PageContainer>
    </>
  );
};
export default CollectionPage;
